/* Responsibility: Untangle FINN.travel code from any specific DOM library */
import bean from 'bean';
import dome from './dome.js';
import fn from './fn.js';

const KEY_ENTER = 13;
const KEY_TAB = 9;

const byId = function (id) {
    const element = document.getElementById(id);
    if (!element) {
        throw new Error(`Missing element with id ${id}`);
    }
    return element;
};

const idExists = function (id) {
    return document.getElementById(id);
};

const hasClass = dome.cn.has;
const byClass = dome.byClass;
const removeClass = dome.cn.rm;
const addClass = dome.cn.add;

const byQuerySelector = function (selector, root) {
    if (!root) {
        return [];
    }
    const elements = root.querySelectorAll(selector);
    return fn.map((el) => el, elements);
};

const toggleClass = function (className, el) {
    if (hasClass(className, el)) {
        removeClass(className, el);
    } else {
        addClass(className, el);
    }
};

const closestWithClass = function (className, el) {
    if (el === null) {
        throw new TypeError(`Closest element with class '${className}' not found`);
    }
    if (!hasClass(className, el)) {
        return closestWithClass(className, el.parentNode);
    }
    return el;
};

const closestWithTagName = function (tagName, el) {
    if (el === null || typeof el.tagName === 'undefined') {
        throw new TypeError(`Closest tag '${tagName}' not found`);
    }
    if (el.tagName.toLowerCase() !== tagName.toLowerCase()) {
        return closestWithTagName(tagName, el.parentNode);
    }
    return el;
};

const closestWithAttribute = function (attribute, el) {
    if (el === null) {
        throw new TypeError(`Closest element with attribute '${attribute}' not found`);
    }
    if (!el.hasAttribute(attribute, el)) {
        return closestWithAttribute(attribute, el.parentNode);
    }
    return el;
};

const children = dome.children;

const prependChild = function (el, root) {
    if (root.childNodes.length) {
        root.insertBefore(el, root.firstChild);
    } else {
        root.appendChild(el);
    }
};

const insertBefore = function (el, next) {
    next.parentNode.insertBefore(el, next);
};

const insertAfter = function (el, before) {
    if (before.nextSibling) {
        before.parentNode.insertBefore(el, before.nextSibling);
    } else {
        before.parentNode.appendChild(el);
    }
};

const remove = function (el) {
    el.parentNode.removeChild(el);
};

const on = bean.on;

const onKeyChange = function (input, callback) {
    let value = input.value;
    this.on(input, 'input keyup', (e) => {
        // Ignore enter and tab, to avoid triggering changes
        // when choosing from a list of completions
        // instead of actual user key presses.
        if (e.keyCode === KEY_ENTER || e.keyCode === KEY_TAB) {
            return;
        }

        setTimeout(() => {
            if (input.value !== value) {
                value = input.value;
                callback();
            }
        }, 1);
    });
};

const position = function (element) {
    const target = element;
    const targetWidth = target.offsetWidth;
    const targetHeight = target.offsetHeight;
    let gleft = 0;
    let gtop = 0;
    let rect;

    const moonwalk = function (parent) {
        if (parent) {
            gleft += parent.offsetLeft;
            gtop += parent.offsetTop;
            moonwalk(parent.offsetParent);
        } else {
            rect = {
                top: target.offsetTop + gtop,
                left: target.offsetLeft + gleft,
                bottom: target.offsetTop + gtop + targetHeight,
                right: target.offsetLeft + gleft + targetWidth,
            };
        }
    };

    moonwalk(target.offsetParent);
    return rect;
};

const siblings = function (element) {
    return fn.select((item) => item !== element, children(element.parentNode));
};

const siblingsWithClass = function (element, className) {
    return fn.select((item) => hasClass(className, item), siblings(element));
};

export default {
    byId,
    idExists,
    hasClass,
    byClass,
    removeClass,
    addClass,
    byQuerySelector,
    toggleClass,
    closestWithClass,
    closestWithTagName,
    closestWithAttribute,
    children,
    prependChild,
    insertBefore,
    insertAfter,
    remove,
    on,
    off: bean.off,
    fire: bean.fire,
    onKeyChange,
    position,
    siblings,
    siblingsWithClass,
    data: dome.data,
};
